import {
  SensorType,
  extractReadingValueFromSensorEvent,
  AirQualityReading,
  AirQualityThresholds,
} from 'common';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { YStack, Text } from 'tamagui';
import { gradients } from 'tamagui-config';
import { tokens } from 'tamagui-config/src/tokens';

import { TripleGlowBackground } from '../../../components/common';
import { useLatestSensorEvents } from '../../../hooks/sensors/useLatestSensorEvent';

interface AirQualityStatusCardProps {
  type: 'pm25' | 'iaq';
}

export const AirQualityStatusCard: React.FC<AirQualityStatusCardProps> = ({ type }) => {
  const { t } = useTranslation();
  const { event } = useLatestSensorEvents(SensorType.AIR_QUALITY);

  const { value, title, subtitle, gradientColors } = React.useMemo(() => {
    if (!event) {
      return {
        value: 0,
        title: t('air.noData'),
        subtitle: type === 'pm25' ? 'PM2.5' : 'IAQ',
        gradientColors: gradients.error,
      };
    }

    const reading = extractReadingValueFromSensorEvent<AirQualityReading>(event);
    if (!reading) {
      return {
        value: 0,
        title: t('air.noData'),
        subtitle: type === 'pm25' ? 'PM2.5' : 'IAQ',
        gradientColors: gradients.error,
      };
    }

    if (type === 'pm25') {
      const value = reading.pm;
      const thresholds = AirQualityThresholds.pm25;

      let airQualityLabel = '';
      let gradientColors = gradients.success;

      if (value < 0) {
        airQualityLabel = t('common.sensorFault');
        gradientColors = gradients.error;
      } else if (value <= thresholds.good) {
        airQualityLabel = t('airQuality.statusGood');
        gradientColors = gradients.success;
      } else if (value <= thresholds.moderate) {
        airQualityLabel = t('airQuality.statusModerate');
        gradientColors = gradients.warning;
      } else {
        airQualityLabel = t('airQuality.statusPoor');
        gradientColors = gradients.error;
      }

      return {
        value,
        title: airQualityLabel,
        subtitle: t('airQuality.pm25'),
        gradientColors,
      };
    } else {
      // IAQ - using mobile approach with quality ranges array
      const value = reading.iaq;
      const thresholds = AirQualityThresholds.iaq;

      let airQualityLabel: string;
      let gradientColors: string[];

      if (value < 0) {
        airQualityLabel = t('common.sensorFault');
        gradientColors = gradients.error;
      } else {
        // Air quality ranges ordered from best to worst (same as mobile)
        const qualityRanges = [
          {
            threshold: thresholds.excellent,
            labelKey: 'airQuality.statusExcellent',
            gradientColors: gradients.success,
          },
          {
            threshold: thresholds.good,
            labelKey: 'airQuality.statusGood',
            gradientColors: gradients.success,
          },
          {
            threshold: thresholds.lightlyPolluted,
            labelKey: 'airQuality.statusLightlyPolluted',
            gradientColors: gradients.warning,
          },
          {
            threshold: thresholds.moderatelyPolluted,
            labelKey: 'airQuality.statusModeratelyPolluted',
            gradientColors: gradients.warning,
          },
          {
            threshold: thresholds.heavilyPolluted,
            labelKey: 'airQuality.statusHeavilyPolluted',
            gradientColors: gradients.error,
          },
          {
            threshold: thresholds.severelyPolluted,
            labelKey: 'airQuality.statusSeverelyPolluted',
            gradientColors: gradients.error,
          },
        ];

        // Find the appropriate quality range or default to worst case
        const qualityRange = qualityRanges.find((range) => value <= range.threshold) || {
          labelKey: 'airQuality.statusExtremelyPolluted',
          gradientColors: gradients.error,
        };

        airQualityLabel = t(qualityRange.labelKey as any);
        gradientColors = qualityRange.gradientColors;
      }

      return {
        value,
        title: airQualityLabel,
        subtitle: t('airQuality.iaq'),
        gradientColors,
      };
    }
  }, [event, type, t]);

  return (
    <YStack position={'relative'} flex={1}>
      <TripleGlowBackground gradientColors={gradientColors} />
      <YStack justifyContent="center" flex={1} alignItems="center" gap="$4">
        <Text
          fontFamily="$heading"
          fontWeight="$9.5"
          fontSize="$8"
          textAlign="center"
          color={tokens.color.white12}>
          {title}
        </Text>

        <Text fontSize="$6" color={tokens.color.white10} textAlign="center">
          {`${value} - ${subtitle}`}
        </Text>
      </YStack>
    </YStack>
  );
};
